import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './dynamic-media-video.module.scss';
import { generateSrc } from './generate-src';
import { VideoElementType } from '../types';

export type DynamicMediaVideoType = {
  className?: string;
  videoControls?: boolean;
  video: VideoElementType;
  isInView: boolean;
  altText: string;
  withoutCenteredButton?: boolean;
  fetchPriority?: string;
  hasDarkeningLayer?: boolean;
};

const baseClass = 'dynamic-media-video';
const presetName = '$smartResponsiveHiDPI$';

/**
 * @param {isInView} isInView you can use the useInitialIntersection() hook to set when the autoPlay prop should be true.
 */

export const DynamicMediaVideo = React.forwardRef<HTMLIFrameElement, DynamicMediaVideoType>(
  (
    {
      className,
      videoControls = true,
      video,
      altText,
      isInView = false,
      withoutCenteredButton,
      fetchPriority,
      hasDarkeningLayer
    },
    ref
  ) => {
    const classes = classnames(styles[baseClass], className);
    const [avsSuffix, setAvsSuffix] = useState('');

    const posterUrlWithPreset = `${video.posterImageUrl}?${presetName}`;

    const updatedVideoProps = {
      ...video,
      posterImageUrl: posterUrlWithPreset
    };

    useEffect(() => {
      const width = window.innerWidth;

      if (width <= 360) {
        setAvsSuffix('-0x360-730k');
        return;
      }

      if (width <= 540) {
        setAvsSuffix('-0x540-2000k');
        return;
      }

      if (width <= 720) {
        setAvsSuffix('-0x720-3000k');
        return;
      }

      setAvsSuffix('-0x1080-5000k');
    }, []);

    const videoSrc =
      isInView && avsSuffix
        ? generateSrc(updatedVideoProps, videoControls, avsSuffix, withoutCenteredButton, hasDarkeningLayer)
        : 'about:blank';

    return (
      <iframe
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allow="fullscreen"
        ref={ref}
        className={classes}
        src={videoSrc}
        title="Video"
        aria-label={altText}
        scrolling="no"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fetchpriority={fetchPriority}
        data-testid="dynamicmediavideo-component"
      />
    );
  }
);

DynamicMediaVideo.displayName = 'DynamicMediaVideo';
